import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Layout from '../../componentsv2/Layout';
import Typography from '../../componentsv2/Typography';
import { LLCIcon, ComplianceIcon, FactoringCheckIcon } from '../../components/Icons';
import { getHolidays } from '../../services/products';
import { setProductStatus, setSelectedCompany, useAppDispatch, useAppSelector } from '../../store';
import { useUserLocation } from '../../hooks/useUserLocation';
import { useLazyTranslation } from '../../hooks/i18n';
import { formatDate } from '../../utils/common';
import { BANKING, COMPLIANCE, INVOICE_FINANCING } from '../../constants/auth';
import { Nullable } from '../../types';
import { Holiday } from '../../types/holidays';
import { CompanyOnboardingStep, CompanyUser } from '../../types/companyUser';
import { homeEvents } from '../../constants/events';
import { COLORS } from '../../themev2';
import ClientDashboardSkeleton from './client/skeleton';
import HolidayNotice from './HolidayNotice';
import ActivationBanner from './ActivationBanner';
import OnboardingTracker from './OnboardingTracker';
import ComplianceBanner from './ComplianceBanner';
import ComplianceCard from './client/ComplianceCard';
import TotalBalanceCard from './client/TotalBalanceCard';
import NonActiveCreditLineCard from './client/NonActiveCreditLineCard';
import { HoverArrowIcon } from './client/icons';
import BankingActions from './BankingActions';
import CreditLineCard from '../RevolvingCreditLine/CreditLineCard';
import { useRevolvingCreditLineList, useRevolvingCreditLineDetail } from '../../hooks/useRevolvingCreditLine';
import { useBankingDetails, usePendingRfis } from '../../hooks/useBanking';
import RFIActionRequiredBanner from '../../componentsv2/RFIActionRequiredBanner';

import AccountBalancesTable from '../Banking/Dashboard/AccountBalancesTable';
import RecentTransactionsTable from '../Banking/Dashboard/RecentTransactionsTable';
import { useRecentTransactions } from '../../hooks/useRecentTransactions';
import { isActivatingGlobalAccount, isGlobalAccountActive } from '../../utils/onboarding';

const PRODUCTS = [
  {
    title: 'application:dashboard.products.globalAccount.title',
    description: 'application:dashboard.products.globalAccount.description',
    Icon: LLCIcon,
    linkTo: BANKING,
  },
  {
    title: 'application:dashboard.products.factoring.title',
    description: 'application:dashboard.products.factoring.description',
    Icon: FactoringCheckIcon,
    linkTo: INVOICE_FINANCING,
  },
  {
    title: 'application:dashboard.products.compliance.title',
    description: 'application:dashboard.products.compliance.description',
    Icon: ComplianceIcon,
    linkTo: COMPLIANCE,
  },
];

export default function ClientDashboard() {
  const now = new Date();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading: isLoadingTranslations, t } = useLazyTranslation([
    'common',
    'banking',
    'application',
    'servicing',
    'banking-page',
    'revolving-credit-line',
  ]);
  const { locale, language } = useUserLocation();
  const { user } = useAppSelector((state) => state.auth);
  const { selectedCompany, draftCompany, companies } =
    useAppSelector((state) => state.companyUser.companyUser) || ({} as CompanyUser);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  // if companyId is a queryParam, there is a useEffect that sets that company as selected company
  const companyId = queryParams.get('companyId');
  const [holiday, setHoliday] = useState<Nullable<Holiday>>(null);
  const hasComplianceEnabled = selectedCompany?.productStatus?.readyForCompliance;
  const readyForBanking = selectedCompany?.productStatus?.readyForBanking;
  const readyForBankingAccountCreation = selectedCompany?.productStatus?.readyForBankingAccountCreation;
  const isBankingActive = isGlobalAccountActive(selectedCompany);
  const getTrackerCompany = () => {
    if (isActivatingGlobalAccount(selectedCompany)) return selectedCompany;
    if (draftCompany?.onboardingStep === CompanyOnboardingStep.Completed) return draftCompany;
    return null;
  };
  const trackerCompany = getTrackerCompany();
  const { data: creditLineList } = useRevolvingCreditLineList(selectedCompany?.id);
  const hasActiveCreditLine = creditLineList && creditLineList.results.length > 0;
  const creditLineId = creditLineList?.results[0]?.id;
  const { data: creditLineDetail } = useRevolvingCreditLineDetail(selectedCompany?.id, creditLineId);

  const bankingAccountId = selectedCompany?.accounts?.[0]?.id;
  const canFetchBankingInfo = readyForBanking || readyForBankingAccountCreation;
  const {
    data: bankingInfo,
    isFetching: isLoadingBankingInfo,
    isInitialLoading: isInitialLoadingBankingInfo,
  } = useBankingDetails({
    companyId: canFetchBankingInfo ? selectedCompany?.id : undefined,
    accountId: canFetchBankingInfo ? bankingAccountId : undefined,
  });

  const { data: recentTransactions = [], isFetching: isLoadingRecentTransactions } = useRecentTransactions({
    companyId: isBankingActive ? selectedCompany?.id : undefined,
    accountId: isBankingActive ? bankingAccountId : undefined,
  });

  const { data: pendingRfis } = usePendingRfis({
    companyId: selectedCompany?.id,
    accountId: bankingAccountId,
  });

  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>(['USD', 'MXN', 'EUR']);

  useEffect(() => {
    if (companyId) {
      setSelectedCompany(companyId);
    }
  }, [companyId]);

  // Set the holidays
  useEffect(() => {
    selectedCompany &&
      getHolidays(selectedCompany.id).then((holidays) => {
        setHoliday(holidays.length ? holidays[0] : null);
      });
  }, [selectedCompany]);

  useEffect(() => {
    logEvent(homeEvents.VIEW_PAGE);
  }, []);

  // Set default currencies from bankingInfo or use defaults
  useEffect(() => {
    if (
      bankingInfo?.globalAccountListSettings &&
      bankingInfo.globalAccountListSettings.settings.globalAccountCurrencies
    ) {
      setSelectedCurrencies(bankingInfo.globalAccountListSettings.settings.globalAccountCurrencies);
    }
  }, [bankingInfo]);

  if (isLoadingTranslations) return <ClientDashboardSkeleton />;
  return (
    <Layout>
      <Grid container rowSpacing={2}>
        <Grid
          item
          xs={12}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'initial' },
            display: { xs: 'none', md: 'flex' },
          }}
        >
          <Typography variant="h5" fontWeight="700">
            {`${t('common:welcome')}, ${user?.firstName}`}
          </Typography>
          <Typography fontWeight={600}>{formatDate(now, locale)}</Typography>
        </Grid>

        <ComplianceBanner selectedCompany={selectedCompany} />

        <RFIActionRequiredBanner rfis={pendingRfis} />

        <ActivationBanner draftCompany={draftCompany} companiesLength={companies?.length ?? 0} />
        {trackerCompany && (
          <OnboardingTracker
            company={trackerCompany}
            bankingStatus={trackerCompany === selectedCompany ? bankingInfo?.status : undefined}
            isLoading={trackerCompany === selectedCompany && isInitialLoadingBankingInfo}
            onStart={(updatedProductStatus) => dispatch(setProductStatus({ ...updatedProductStatus }))}
          />
        )}
        <HolidayNotice holiday={holiday} />

        {isBankingActive && (
          <>
            <BankingActions bankingInfo={bankingInfo} t={t} />

            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <TotalBalanceCard
                totalBalance={bankingInfo?.aggregatedBalance ?? undefined}
                isLoading={isLoadingBankingInfo}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <AccountBalancesTable
                accountBalances={bankingInfo?.accountBalances ?? []}
                selectedCurrencies={selectedCurrencies}
                isLoading={isLoadingBankingInfo}
                compact
                t={t}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              {hasActiveCreditLine ? (
                <CreditLineCard creditLine={creditLineDetail} compactMode={true} t={t} />
              ) : (
                <NonActiveCreditLineCard />
              )}
            </Grid>
          </>
        )}

        {isBankingActive && (
          <Grid item xs={12}>
            <RecentTransactionsTable
              transactions={recentTransactions}
              isLoading={isLoadingRecentTransactions}
              language={language}
              t={t}
            />
          </Grid>
        )}

        {hasComplianceEnabled && (
          <>
            <Grid item xs={12} md={6}>
              <ComplianceCard selectedCompany={selectedCompany} />
            </Grid>
            <Grid item md={6} sx={{ display: { xs: 'none', md: 'block' } }} />
          </>
        )}
        {PRODUCTS.map(({ title, description, Icon, linkTo }) => (
          <Grid item xs={12} md={4} key={title}>
            <Box
              onClick={() => navigate(linkTo)}
              sx={{
                padding: '2em',
                backgroundColor: trackerCompany ? COLORS.gray[100] : '#FBFCFE',
                borderRadius: '12px',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                boxSizing: 'border-box',
                cursor: 'pointer',
                '& .illustration-default': { display: 'block' },
                '& .illustration-hover': { display: 'none' },
                '&:hover .illustration-default': { display: trackerCompany ? 'block' : 'none' },
                '&:hover .illustration-hover': { display: trackerCompany ? 'none' : 'flex' },
              }}
            >
              <Box
                sx={{
                  width: '65%',
                  zIndex: 2,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Typography
                    fontWeight={700}
                    sx={{
                      fontSize: '18px',
                      lineHeight: '28px',
                      color: trackerCompany ? COLORS.gray[1100] : COLORS.brand.night,
                    }}
                  >
                    {t(title)}
                  </Typography>
                  {trackerCompany && <LockOutlinedIcon sx={{ fontSize: '16px', color: COLORS.gray[1000] }} />}
                </Box>
                <Typography
                  variant="body1"
                  sx={{ color: trackerCompany ? COLORS.gray[1000] : COLORS.gray[1100] }}
                >
                  {t(description)}
                </Typography>
              </Box>
              <Box
                className="illustration-default"
                sx={{ position: 'absolute', right: 0, bottom: 0, zIndex: 1 }}
              >
                <Icon />
              </Box>
              <Box
                className="illustration-hover"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: '2em',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                }}
              >
                <HoverArrowIcon />
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Layout>
  );
}
